import { DbKey } from '../adl-gen/common/db';
import {
  AppClient,
  AppUser,
  Competency,
  Question,
  Quiz,
  Skill,
  TrainingOffering,
  TrainingOfferingCategory,
  UserGroup,
  UserQuiz,
  WorkRole
} from '../adl-gen/totalinteraction/futurefit/db';
import { SelectedResults, Settings } from '../stores/manager-quiz-results-store';
import { LearningType } from '../stores/quiz-results-store';

/** A typesafe route */

export type Route =
  | { route: 'login' }
  | { route: 'logout' }
  | { route: 'home' }
  | { route: 'my-assessments' }
  | { route: 'quiz'; userQuizId: DbKey<UserQuiz> }
  | { route: 'self-assessment'; userQuizId: DbKey<UserQuiz> }
  | {
      route: 'quiz-results';
      quizId: DbKey<Quiz>;
      userQuizId: DbKey<UserQuiz>;
      page: 'profile' | 'guidance' | 'recommendations' | 'print';
      // rolesSelected?: string;
      /// FF2-427/FF2-429 for guidance/recsAvailable - and also put rolesSelected in here
      settingsJson?: string;
    }
  | {
      route: 'manager';
    }
  | {
      route: 'manager-quiz-results';
      quizId: DbKey<Quiz>;
      page: 'profile' | 'guidance' | 'recommendations' | 'print';
      settingsJson?: string;
    }
  | {
      route: 'admin';
      page:
        | 'clients'
        | 'competencies-skills'
        | 'groups'
        | 'users'
        | 'roles'
        | 'reports'
        | 'questions'
        | 'recommendations'
        | 'client'
        | 'user'
        | 'group'
        | 'competency'
        | 'skill'
        | 'role'
        | 'role-builder'
        | 'quiz-admin'
        | 'question'
        | 'training-offering';
      view?: 'new' | 'update' | 'clone' | 'upload' | 'upload-results';
      appClientId?: DbKey<AppClient>;
      id?:
        | DbKey<AppUser>
        | DbKey<UserGroup>
        | DbKey<Competency>
        | DbKey<Skill>
        | DbKey<WorkRole>
        | DbKey<Question>
        | DbKey<TrainingOffering>;
      skillId?: DbKey<Skill>;
      workRoleId?: DbKey<WorkRole>;
    };

export function routeFromPath(path: string): Route | undefined {
  if (path.startsWith('/')) {
    const parts = path
      .substring(1)
      // deprecated
      // .substr(1)
      .split('/')
      .map(decodeURIComponent);

    /// ----- [ parts.length = 1 ] --------------------------------
    // /route -> route | home
    if (parts.length === 1) {
      switch (parts[0]) {
        case 'login':
          return { route: 'login' };
        case 'logout':
          return { route: 'logout' };
        case 'home':
          return { route: 'home' };
        case 'my-assessments':
          return { route: 'my-assessments' };
        case 'manager':
          return { route: 'manager' };
        // case "reports": return { route: "reports" };
        case 'admin':
          return { route: 'home' };
        default:
          return undefined;
      }
    }

    /// ----- [ parts.length = 2 ] --------------------------------
    // /quiz/id -> quiz, id
    // /self-assessment/id -> self-assessment, id
    // /admin/clients -> admin-clients, clients
    // /admin/competencies-skills -> competencies-skills
    // /admin/roles -> roles (superuser)
    // /admin/reports -> reports (superuser) with no client selected
    // /admin/questions -> questions
    //TODO: /admin/users -> super-users
    else if (parts.length === 2) {
      switch (parts[0]) {
        case 'quiz':
          return { route: 'quiz', userQuizId: parts[1] };
        case 'self-assessment':
          return { route: 'self-assessment', userQuizId: parts[1] };

        case 'admin': {
          const page = parts[1];
          switch (page) {
            case 'clients':
            case 'competencies-skills':
            case 'roles':
            case 'reports':
            case 'questions':
            case 'recommendations':
              return { route: 'admin', page };
            // case "recommendations":
            default:
              return undefined;
          }
          // if (
          //   // page === "recommendations" ||
          // ) {

          // } else { return undefined; }
        }

        default:
          return undefined;
      }
    } else {
      /// parts.length > 2
      switch (parts[0]) {
        /// ----- [ 4 <= parts.length <= 5 ] -------------------------------
        // /quiz-results/qid/uqid/page[/settings]
        case 'quiz-results': {
          if (parts.length !== 4 && parts.length !== 5) {
            return undefined;
          } else {
            const page = parts[3];
            if (page === 'profile' || page === 'guidance' || page === 'recommendations' || page === 'print') {
              const settingsB64 = parts.length === 5 ? parts[4] : undefined;
              let settingsJson;
              if (settingsB64 !== undefined) {
                settingsJson = atob(settingsB64);
              }
              return {
                route: 'quiz-results',
                quizId: parts[1],
                userQuizId: parts[2],
                page,
                settingsJson
              };
            } else {
              return undefined;
            }
          }
        }

        /// ----- [ 3 <= parts.length <= 4 ] -------------------------------
        // /manager-quiz-results/qid/page[/settings]
        case 'manager-quiz-results': {
          if (parts.length !== 3 && parts.length !== 4) {
            return undefined;
          } else {
            const page = parts[2];
            const settingsB64 = parts.length === 4 ? parts[3] : undefined;
            let settingsJson;
            if (settingsB64 !== undefined) {
              settingsJson = atob(settingsB64);
            }
            if (page === 'profile' || page === 'guidance' || page === 'recommendations' || page === 'print') {
              return {
                route: 'manager-quiz-results',
                quizId: parts[1],
                page,
                settingsJson
              };
            } else {
              return undefined;
            }
          }
        }

        /// ----- [ admin ] -----------------------------------------------------------------------------------
        /// parts[0]  parts[1]                               parts[2]        parts[3]        parts[4]  parts[5]
        /// ---------------------------------------------------------------------------------------------------
        //  ===== [ superuser ] ===============================================================================
        //  admin     client                                 new
        //  admin     competency|skill|role|role-builder     new
        //  admin     competency|skill|role|role-builder     [id]
        //  admin     competency|skill|role|role-builder     [id]            update
        //  admin     role                                   [id]            clone
        //  admin     quiz-admin                             [workRoleId]    new
        //  admin     question                               new
        //  admin     question                               [skillId]       new
        //  admin     question                               [id]
        //  admin     question                               [id]            update
        //  admin     training-offering                      new
        //  admin     training-offering                      [id]
        //  admin     training-offering                      [id]            update
        //  ===== [ client admin user ] =======================================================================
        //  admin     client                                 [id]            update
        //  admin     role|role-builder                      [clientid]      new
        //  admin     role|role-builder                      [clientid]      [id]
        //  admin     role|role-builder                      [clientid]      [id]            update
        //  admin     role                                   [clientid]      [id]            clone
        //  admin     quiz-admin                             [clientid]      [workRoleId]    new
        //  ===== [ both ] ====================================================================================
        //  admin     client                                 [id]
        //  admin     client                                 [id]            update
        //  admin     users|groups                           [clientid]
        //  admin     user|group                             [clientid]      new
        //  admin     user|group                             [clientid]      [id]
        //  admin     user|group                             [clientid]      [id]            update
        //  admin     user                                   [clientid]      upload
        //  admin     user                                   [clientid]      [id]            upload-results
        //  admin     roles                                  [clientid]
        //  admin     reports                                [clientid]
        // TODO: /admin/client|competency|skill|role|question|training-offering/id/delete
        // TODO: /admin/user|group/clientid/id/delete
        // TODO: /admin/quiz-admin/id[/update/delete]
        // TODO: /admin/report/new|[id]/[update|delete]
        case 'admin': {
          const page = parts[1];
          switch (page) {
            case 'client': {
              if (parts.length !== 3 && parts.length !== 4) {
                return undefined;
              }
              if (parts.length === 3) {
                if (parts[2] === 'new') {
                  const view = parts[2];
                  return { route: 'admin', page, view };
                } else {
                  const appClientId = parts[2];
                  const id = parts[2];
                  return { route: 'admin', page, appClientId, id };
                }
              } else {
                const appClientId = parts[2];
                const view = parts[3];
                const id = parts[2];
                if (view === 'update') {
                  return { route: 'admin', page, view, appClientId, id };
                } else {
                  return undefined;
                }
              }
            }

            case 'competency':
            case 'skill': {
              if (parts.length !== 3 && parts.length !== 4) {
                return undefined;
              }
              if (parts.length === 3) {
                if (parts[2] === 'new') {
                  const view = parts[2];
                  return { route: 'admin', page, view };
                } else {
                  const id = parts[2];
                  return { route: 'admin', page, id };
                }
              } else {
                const id = parts[2];
                const view = parts[3];
                if (view === 'update') {
                  return { route: 'admin', page, view, id };
                } else {
                  return undefined;
                }
              }
            }

            case 'question': {
              console.debug('DEBUG routefrompath question', parts);
              if (parts.length !== 3 && parts.length !== 4) {
                return undefined;
              }
              if (parts.length === 3) {
                if (parts[2] === 'new') {
                  const view = parts[2];
                  console.debug('DEBUG routefrompath returning', { route: 'admin', page, view });
                  return { route: 'admin', page, view };
                } else {
                  const id = parts[2];
                  return { route: 'admin', page, id };
                }
              } else {
                const view = parts[3];
                if (view === 'new') {
                  const skillId = parts[2];
                  return { route: 'admin', page, view, skillId };
                } else if (view === 'update') {
                  const id = parts[2];
                  return { route: 'admin', page, view, id };
                } else {
                  return undefined;
                }
              }
            }

            case 'training-offering': {
              console.debug('DEBUG routefrompath training-offering', parts);
              if (parts.length !== 3 && parts.length !== 4) {
                return undefined;
              }
              if (parts.length === 3) {
                if (parts[2] === 'new') {
                  const view = parts[2];
                  console.debug('DEBUG routefrompath returning', { route: 'admin', page, view });
                  return { route: 'admin', page, view };
                } else {
                  const id = parts[2];
                  return { route: 'admin', page, id };
                }
              } else {
                const view = parts[3];
                if (view === 'update') {
                  const id = parts[2];
                  return { route: 'admin', page, view, id };
                } else {
                  return undefined;
                }
              }
            }

            case 'role': {
              if (parts.length < 3 && parts.length > 5) {
                return undefined;
              }
              if (parts.length === 3) {
                if (parts[2] === 'new') {
                  const view = parts[2];
                  return { route: 'admin', page, view };
                } else {
                  const id = parts[2];
                  return { route: 'admin', page, id };
                }
              } else if (parts.length === 4) {
                if (parts[3] === 'update' || parts[3] === 'clone') {
                  const id = parts[2];
                  const view = parts[3];
                  return { route: 'admin', page, view, id };
                } else {
                  const appClientId = parts[2];
                  if (parts[3] === 'new') {
                    const view = parts[3];
                    return { route: 'admin', page, view, appClientId };
                  } else {
                    const id = parts[3];
                    return { route: 'admin', page, id, appClientId };
                  }
                }
              } else if (parts.length === 5) {
                const appClientId = parts[2];
                const id = parts[3];
                if (parts[4] === 'update' || parts[4] === 'clone') {
                  const view = parts[4];
                  return { route: 'admin', page, view, appClientId, id };
                } else {
                  return undefined;
                }
              }
            }

            case 'role-builder': {
              if (parts.length < 3 && parts.length > 5) {
                return undefined;
              }
              if (parts.length === 3) {
                if (parts[2] === 'new') {
                  const view = parts[2];
                  return { route: 'admin', page, view };
                } else {
                  const id = parts[2];
                  return { route: 'admin', page, id };
                }
              } else if (parts.length === 4) {
                if (parts[3] === 'update') {
                  const id = parts[2];
                  const view = parts[3];
                  return { route: 'admin', page, view, id };
                } else {
                  const appClientId = parts[2];
                  if (parts[3] === 'new') {
                    const view = parts[3];
                    return { route: 'admin', page, view, appClientId };
                  } else {
                    const id = parts[3];
                    return { route: 'admin', page, id, appClientId };
                  }
                }
              } else if (parts.length === 5) {
                const appClientId = parts[2];
                const id = parts[3];
                if (parts[4] === 'update') {
                  const view = parts[4];
                  return { route: 'admin', page, view, appClientId, id };
                } else {
                  return undefined;
                }
              }
            }

            case 'users':
            case 'groups':
            case 'reports':
            case 'roles': {
              if (parts.length !== 3 /*&& parts.length !== 4*/) {
                return undefined;
              }
              if (parts.length === 3) {
                const appClientId = parts[2];
                return { route: 'admin', page, appClientId };
              }
            }

            case 'user': {
              if (parts.length !== 4 && parts.length !== 5) {
                return undefined;
              }
              if (parts.length === 4) {
                const appClientId = parts[2];
                if (parts[3] === 'new' || parts[3] === 'upload') {
                  const view = parts[3];
                  return { route: 'admin', page, appClientId, view };
                } else {
                  const id = parts[3];
                  return {
                    route: 'admin',
                    page,
                    appClientId,
                    id
                  };
                }
              } else {
                const appClientId = parts[2];
                const id = parts[3];
                const view = parts[4];
                if (view === 'update' || view === 'upload-results') {
                  console.debug(
                    'DEBUG Routefrompath returning route page view appClientId id',
                    'admin',
                    page,
                    view,
                    appClientId,
                    id
                  );
                  return { route: 'admin', page, view, appClientId, id };
                } else {
                  return undefined;
                }
              }
            }

            case 'group': {
              if (parts.length !== 4 && parts.length !== 5) {
                return undefined;
              }
              if (parts.length === 4) {
                const appClientId = parts[2];
                if (parts[3] === 'new') {
                  const view = parts[3];
                  return { route: 'admin', page, appClientId, view };
                } else {
                  const id = parts[3];
                  return {
                    route: 'admin',
                    page,
                    appClientId,
                    id
                  };
                }
              } else {
                const appClientId = parts[2];
                const id = parts[3];
                const view = parts[4];
                if (view === 'update' || view === 'upload-results') {
                  return { route: 'admin', page, view, appClientId, id };
                } else {
                  return undefined;
                }
              }
            }
            case 'quiz-admin': {
              if (parts.length !== 4 && parts.length !== 5) {
                return undefined;
              }
              if (parts.length === 4) {
                const workRoleId = parts[2];
                if (parts[3] === 'new') {
                  const view = parts[3];
                  return { route: 'admin', page, workRoleId, view };
                }
                // TODO: update/delete/view; in the meantime
                else {
                  return undefined;
                }
              } else {
                const appClientId = parts[2];
                const workRoleId = parts[3];
                if (parts[4] === 'new') {
                  const view = parts[4];
                  return { route: 'admin', page, appClientId, workRoleId, view };
                }
                // TODO: update/delete/view; in the meantime
                else {
                  return undefined;
                }
              }
            }
            // case "recommendations":
            default:
              return undefined;
          }
        }
      }
    }
  }
  return undefined;
}

export function pathFromRoute(route: Route): string {
  switch (route.route) {
    case 'home':
      return '/';
    case 'my-assessments':
      return '/my-assessments';
    case 'logout':
      return '/logout';
    case 'login':
      return '/login';
    case 'quiz':
      return '/quiz/' + encodeURIComponent(route.userQuizId);
    case 'self-assessment':
      return '/self-assessment/' + encodeURIComponent(route.userQuizId);
    case 'quiz-results': {
      const baseRoute =
        '/quiz-results/' +
        encodeURIComponent(route.quizId) +
        '/' +
        encodeURIComponent(route.userQuizId) +
        '/' +
        route.page;
      if (route.settingsJson !== undefined) {
        const settingsB64 = btoa(route.settingsJson);
        const settingsJson = encodeURIComponent(settingsB64);
        return baseRoute + '/' + settingsJson;
      }
      return baseRoute;
    }
    case 'manager':
      return '/manager';
    case 'manager-quiz-results': {
      const baseRoute = '/manager-quiz-results/' + encodeURIComponent(route.quizId) + '/' + route.page;
      if (route.settingsJson !== undefined) {
        const settingsB64 = btoa(route.settingsJson);
        const settingsJson = encodeURIComponent(settingsB64);
        return baseRoute + '/' + settingsJson;
      }
      return baseRoute;
    }

    case 'admin': {
      switch (route.page) {
        case 'client': {
          if (route.view) {
            if (route.view === 'new') {
              return `/admin/${route.page}/${route.view}`;
            } else if (route.appClientId) {
              return `/admin/${route.page}/${route.appClientId}/${route.view}`;
            }
          } else if (route.appClientId) {
            return `/admin/${route.page}/${route.appClientId}`;
          }
          /// Should automatically redirect to home
          return `/admin/${route.page}`;
        }

        case 'competencies-skills':
        case 'questions':
        case 'recommendations':
          return `/admin/${route.page}`;

        case 'users':
        case 'groups':
        case 'roles':
        case 'reports': {
          if (route.appClientId) {
            return `/admin/${route.page}/${route.appClientId}`;
          }
          /// Should automatically redirect to home, unless it's admin/reports and superuser without a selected client
          return `/admin/${route.page}`;
        }

        case 'user': {
          if (route.appClientId) {
            if (route.view) {
              if (route.view === 'new' || route.view === 'upload') {
                return `/admin/${route.page}/${route.appClientId}/${route.view}`;
              } else if (route.id) {
                console.debug(
                  'DEBUG pathfromroute returning ',
                  `/admin/${route.page}/${route.appClientId}/${route.id}/${route.view}`
                );
                return `/admin/${route.page}/${route.appClientId}/${route.id}/${route.view}`;
              }
            } else if (route.id) {
              return `/admin/${route.page}/${route.appClientId}/${route.id}`;
            }
          }
          /// Should automatically redirect to home
          return `/admin/${route.page}`;
        }

        case 'group': {
          if (route.appClientId) {
            if (route.view) {
              if (route.view === 'new') {
                return `/admin/${route.page}/${route.appClientId}/${route.view}`;
              } else if (route.id) {
                return `/admin/${route.page}/${route.appClientId}/${route.id}/${route.view}`;
              }
            } else if (route.id) {
              return `/admin/${route.page}/${route.appClientId}/${route.id}`;
            }
          }
          /// Should automatically redirect to home
          return `/admin/${route.page}`;
        }

        case 'competency':
        case 'skill': {
          if (route.view) {
            if (route.view === 'new') {
              return `/admin/${route.page}/${route.view}`;
            } else if (route.id) {
              return `/admin/${route.page}/${route.id}/${route.view}`;
            }
          } else if (route.id) {
            return `/admin/${route.page}/${route.id}`;
          }
          /// Should automatically redirect to home
          return `/admin/${route.page}`;
        }

        case 'question': {
          if (route.view) {
            if (route.view === 'new') {
              if (route.skillId) {
                return `/admin/${route.page}/${route.skillId}/${route.view}`;
              } else {
                return `/admin/${route.page}/${route.view}`;
              }
            } else if (route.id) {
              return `/admin/${route.page}/${route.id}/${route.view}`;
            }
          } else if (route.id) {
            return `/admin/${route.page}/${route.id}`;
          }
          /// Should automatically redirect to home
          return `/admin/${route.page}`;
        }

        case 'role':
        case 'role-builder': {
          if (route.appClientId) {
            if (route.view) {
              if (route.view === 'new') {
                return `/admin/${route.page}/${route.appClientId}/${route.view}`;
              } else if (route.id) {
                return `/admin/${route.page}/${route.appClientId}/${route.id}/${route.view}`;
              }
            } else if (route.id) {
              return `/admin/${route.page}/${route.appClientId}/${route.id}`;
            }
          } else {
            if (route.view) {
              if (route.view === 'new') {
                return `/admin/${route.page}/${route.view}`;
              } else if (route.id) {
                return `/admin/${route.page}/${route.id}/${route.view}`;
              }
            } else if (route.id) {
              return `/admin/${route.page}/${route.id}`;
            }
          }
          /// Should automatically redirect to home
          return `/admin/${route.page}`;
        }

        case 'quiz-admin': {
          if (route.appClientId) {
            if (route.view) {
              if (route.view === 'new' && route.workRoleId) {
                return `/admin/${route.page}/${route.appClientId}/${route.workRoleId}/${route.view}`;
              }
            }
          } else {
            if (route.view) {
              if (route.view === 'new' && route.workRoleId) {
                return `/admin/${route.page}/${route.workRoleId}/${route.view}`;
              }
            }
          }
          // TODO: update/delete/view; in the meantime
          /// Should automatically redirect to home
          return `/admin/${route.page}`;
        }

        case 'training-offering': {
          if (route.view) {
            if (route.view === 'new') {
              return `/admin/${route.page}/${route.view}`;
            } else if (route.id) {
              return `/admin/${route.page}/${route.id}/${route.view}`;
            }
          } else if (route.id) {
            return `/admin/${route.page}/${route.id}`;
          }
          /// Should automatically redirect to home
          return `/admin/${route.page}`;
        }

        /// Should automatically redirect to home
        default:
          return `/admin/${route.page}`;
      }
    }
  }
}

// https://stackoverflow.com/a/20392392
export function validJson(jsonString: string): boolean {
  try {
    const o = JSON.parse(jsonString);

    // Handle non-exception-throwing cases:
    // Neither JSON.parse(false) or JSON.parse(1234) throw errors, hence the type-checking,
    // but... JSON.parse(null) returns null, and typeof null === "object",
    // so we must check for that, too. Thankfully, null is falsey, so this suffices:
    if (o && typeof o === 'object') {
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
}

// tslint:disable-next-line: no-any
export function jsonToManagerSettings(obj: any): Settings | undefined {
  const selectedResults = jsonToSelectedResults(obj.filters);
  const role = jsonToString(obj.role);
  const categories = jsonToCategoryArray(obj.categories);
  const competencies = jsonToStringArray(obj.competencies);
  const priorities = jsonToLearningTypeArray(obj.priorities);
  const isGuidanceAvailable = jsonToBoolean(obj.guidanceAvailable);
  const areRecommendationsAvailable = jsonToBoolean(obj.recommendationsAvailable);
  return {
    filters: selectedResults,
    categories,
    competencies,
    priorities,
    role,
    guidanceAvailable: isGuidanceAvailable,
    recommendationsAvailable: areRecommendationsAvailable
  };
}

// tslint:disable-next-line: no-any
export function jsonToSettings(obj: any): Omit<Settings, 'filters'> | undefined {
  const role = jsonToString(obj.role);
  const categories = jsonToCategoryArray(obj.categories);
  const competencies = jsonToStringArray(obj.competencies);
  const priorities = jsonToLearningTypeArray(obj.priorities);
  const isGuidanceAvailable = jsonToBoolean(obj.guidanceAvailable);
  const areRecommendationsAvailable = jsonToBoolean(obj.recommendationsAvailable);
  return {
    categories,
    competencies,
    priorities,
    role,
    guidanceAvailable: isGuidanceAvailable,
    recommendationsAvailable: areRecommendationsAvailable
  };
}

// tslint:disable-next-line: no-any
export function jsonToSelectedResults(obj: any): SelectedResults | undefined {
  if (obj === undefined) {
    return undefined;
  } else if (obj.kind === undefined) {
    return undefined;
  } else if (obj.kind !== 'users' && obj.kind !== 'groups') {
    return undefined;
  } else if (Array.isArray(obj.users) || Array.isArray(obj.groups)) {
    return obj;
  }
  return undefined;
}

// tslint:disable-next-line: no-any
export function jsonToStringArray(obj: any): string[] | undefined {
  if (Array.isArray(obj)) {
    return obj;
  }
  return undefined;
}
// tslint:disable-next-line: no-any
export function jsonToString(obj: any): string | undefined {
  if (typeof obj === 'string') {
    return obj;
  }
  return undefined;
}

// tslint:disable-next-line: no-any
export function jsonToCategoryArray(obj: any): TrainingOfferingCategory[] | undefined {
  if (Array.isArray(obj)) {
    return obj;
  }
  return undefined;
}

// tslint:disable-next-line: no-any
export function jsonToLearningTypeArray(obj: any): LearningType[] | undefined {
  if (Array.isArray(obj)) {
    return obj;
  }
  return undefined;
}

// tslint:disable-next-line: no-any
export function jsonToBoolean(obj: any): boolean | undefined {
  if (obj === false || obj === true) {
    return obj;
  }
  return undefined;
}



// WEBPACK FOOTER //
// ./src/routing/app-routes.ts